/**
 * PostHog telemetry for @spatialwalk/avatarkit-rtc SDK.
 *
 * Reuses the same PostHog named instance as @spatialwalk/avatarkit to avoid
 * duplicate SDK initializations. If avatarkit has already initialized PostHog,
 * this module piggybacks on that instance. Otherwise events are silently dropped.
 *
 * @packageDocumentation
 * @internal
 */

import { logger } from './logger';

// PostHog named instance (must match avatarkit's instance name)
const SDK_POSTHOG_INSTANCE_NAME = 'spatialwalk-posthog';

// Cached PostHog instance
let posthogInstance: any = null;

// SDK metadata
let sdkVersion = '1.0.0';
let appId = '';
let environment = '';

// Event queue for events fired before PostHog is available
interface QueuedEvent {
  event: string;
  level: 'info' | 'warning' | 'error';
  properties: Record<string, unknown>;
}
const eventQueue: QueuedEvent[] = [];
const MAX_QUEUE_SIZE = 50;

// Retry timer for finding PostHog instance
let retryTimer: ReturnType<typeof setInterval> | null = null;
const RETRY_INTERVAL_MS = 500;
const MAX_RETRY_MS = 10000;

// Hostnames to filter (no telemetry)
const FILTERED_HOSTNAMES = ['localhost', '127.0.0.1', '0.0.0.0'] as const;

/**
 * Telemetry configuration options.
 */
export interface TelemetryConfig {
  /** SDK version string */
  sdkVersion?: string;
  /** Application ID */
  appId?: string;
  /** Environment (cn/intl) */
  environment?: string;
}

/**
 * Initialize telemetry by locating the shared PostHog instance from avatarkit.
 * Must be called after AvatarSDK.initialize().
 * @internal
 */
export function initializeTelemetry(config: TelemetryConfig): void {
  if (typeof window === 'undefined') return;

  if (config.sdkVersion) sdkVersion = config.sdkVersion;
  if (config.appId) appId = config.appId;
  if (config.environment) environment = config.environment;

  posthogInstance = getPostHogInstance();
  if (posthogInstance) {
    logger.info('Telemetry', 'PostHog instance found, telemetry enabled');
    flushEventQueue();
  } else {
    logger.info('Telemetry', 'PostHog instance not found yet, will retry');
    startRetry();
  }
}

/**
 * Periodically retry finding the PostHog instance (avatarkit may initialize later).
 * @internal
 */
function startRetry(): void {
  if (retryTimer) return;

  const startTime = Date.now();
  retryTimer = setInterval(() => {
    const instance = getPostHogInstance();
    if (instance) {
      logger.info('Telemetry', 'PostHog instance found after retry, telemetry enabled');
      flushEventQueue();
      stopRetry();
    } else if (Date.now() - startTime > MAX_RETRY_MS) {
      logger.info('Telemetry', 'PostHog instance not found after retries, queued events dropped');
      eventQueue.length = 0;
      stopRetry();
    }
  }, RETRY_INTERVAL_MS);
}

/**
 * Stop the retry timer.
 * @internal
 */
function stopRetry(): void {
  if (retryTimer) {
    clearInterval(retryTimer);
    retryTimer = null;
  }
}

/**
 * Flush queued events to PostHog.
 * @internal
 */
function flushEventQueue(): void {
  if (!posthogInstance || eventQueue.length === 0) return;

  logger.info('Telemetry', `Flushing ${eventQueue.length} queued events`);
  for (const { event, level, properties } of eventQueue) {
    try {
      posthogInstance.capture(event, {
        ...getCommonFields(),
        level,
        ...properties,
      });
    } catch {
      // Ignore flush errors for individual events
    }
  }
  eventQueue.length = 0;
}

/**
 * Try to get the shared PostHog named instance from avatarkit.
 * @internal
 */
function getPostHogInstance(): any {
  if (posthogInstance) return posthogInstance;

  try {
    // PostHog JS SDK stores named instances that can be retrieved
    // We try to get the instance that avatarkit already initialized
    const posthog = (window as any).__posthog;
    if (posthog && typeof posthog === 'object') {
      // Try direct named instance lookup
      const instance = posthog[SDK_POSTHOG_INSTANCE_NAME];
      if (instance && typeof instance.capture === 'function') {
        posthogInstance = instance;
        return instance;
      }
    }

    // Fallback: try posthog-js getInstance API
    try {
      const posthogModule = (window as any).posthog;
      if (posthogModule && typeof posthogModule.getInstance === 'function') {
        const instance = posthogModule.getInstance(SDK_POSTHOG_INSTANCE_NAME);
        if (instance && typeof instance.capture === 'function') {
          posthogInstance = instance;
          return instance;
        }
      }
    } catch {
      // getInstance not available
    }
  } catch {
    // PostHog not available
  }

  return null;
}

/**
 * Check if current hostname should be filtered.
 * @internal
 */
function shouldFilterHostname(): boolean {
  if (typeof window === 'undefined') return false;
  return (FILTERED_HOSTNAMES as readonly string[]).includes(window.location.hostname);
}

/**
 * Get common fields attached to all events.
 * @internal
 */
function getCommonFields(): Record<string, unknown> {
  return {
    platform: 'Web',
    sdk_version: sdkVersion,
    service_module: 'rtc',
    app_id: appId,
    env: environment,
  };
}

/**
 * Track a telemetry event.
 * @param event - Event name
 * @param level - Log level
 * @param properties - Event-specific properties
 * @internal
 */
export function trackEvent(
  event: string,
  level: 'info' | 'warning' | 'error' = 'info',
  properties: Record<string, unknown> = {},
): void {
  if (typeof window === 'undefined') return;
  if (shouldFilterHostname()) return;

  const fullProperties = {
    ...properties,
    timestamp: Date.now(),
  };

  // Try to get instance if not cached yet
  const instance = posthogInstance || getPostHogInstance();
  if (!instance) {
    // Queue event for later
    if (eventQueue.length < MAX_QUEUE_SIZE) {
      eventQueue.push({ event, level, properties: fullProperties });
    }
    return;
  }

  // Instance just became available — flush any queued events first
  if (eventQueue.length > 0) {
    flushEventQueue();
    stopRetry();
  }

  try {
    instance.capture(event, {
      ...getCommonFields(),
      level,
      ...fullProperties,
    });
  } catch (error) {
    logger.warn(
      'Telemetry',
      'Failed to track event:',
      error instanceof Error ? error.message : String(error),
    );
  }
}

/**
 * Flush pending events (for page close / disconnect).
 * @internal
 */
export function flushTelemetry(): void {
  const instance = posthogInstance || getPostHogInstance();
  if (!instance) return;

  try {
    if (typeof instance.flush === 'function') {
      instance.flush();
    }
  } catch {
    // Ignore flush errors
  }
}
